import {useCallback, useMemo} from 'react'
import {TableHead, TableRow, TableCell, Box, Checkbox} from '@mui/material'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import {useControlledFilterTableContainerProps} from './ControlledFilterTableContainer'
import {useControlledFilterTableHelpers} from './useControlledFilterTableUtils'
import {ControlledFilterTableBaseColumn} from './ControlledFilterTable'

export const ControlledFilterTableHeaders = <
  T extends Record<string, any> = Record<string, any>
>() => {
  const {filters, onFilter, columns, selected, onSelectedChange, searchResults, idExtractor, id} =
    useControlledFilterTableContainerProps<T>()
  const {sortedColumn, setSortedColumn} = useControlledFilterTableHelpers({filters, onFilter})

  const isColumnSorted = useCallback(
    (col: ControlledFilterTableBaseColumn<T>) => {
      if (sortedColumn && sortedColumn[0] === (col.sortField || col.field)) {
        return sortedColumn[1] ? true : false
      }
      return null
    },
    [sortedColumn]
  )

  const getSortIcon = useCallback(
    (col: ControlledFilterTableBaseColumn<T>) => {
      if (isColumnSorted(col) !== null) {
        return sortedColumn?.[1] ? (
          <ArrowUpwardIcon fontSize='inherit' />
        ) : (
          <ArrowDownwardIcon fontSize='inherit' />
        )
      }
    },
    [isColumnSorted, sortedColumn]
  )

  const handleColumnSortClick = useCallback(
    (col: ControlledFilterTableBaseColumn<T>) => {
      if (col.sortable) {
        return () => {
          if (isColumnSorted(col) !== null) {
            const isAscending = sortedColumn?.[1]
            setSortedColumn([col.sortField || col.field, !isAscending])
          } else {
            setSortedColumn([col.sortField || col.field, false])
          }
        }
      }
    },
    [isColumnSorted, setSortedColumn, sortedColumn]
  )

  const headerCells = useMemo(() => {
    return columns.map((col) => (
      <TableCell
        id={id && `${id}-header-${col.field}`}
        role={col.sortable ? 'button' : undefined}
        onClick={handleColumnSortClick(col)}
        key={col.field}
        sx={{
          fontWeight: 'bold',
          cursor: col.sortable ? 'pointer' : undefined,
        }}
        align={col.align}
      >
        <Box sx={{display: 'inline-flex', alignItems: 'center'}}>
          {col.label}
          <Box component='span' sx={{ml: 1, display: 'inline-flex', alignItems: 'center'}}>
            {getSortIcon(col)}
          </Box>
        </Box>
      </TableCell>
    ))
  }, [columns, getSortIcon, handleColumnSortClick, id])

  const checkboxState = useMemo((): 'indeterminate' | boolean => {
    if (!searchResults?.data) {
      return false
    }
    if (selected && searchResults?.data) {
      let isAllSelected = true
      let isNoneSelected = true
      for (let item of searchResults.data) {
        const isSelected = selected.some(
          (selectedItem) => idExtractor(item) === idExtractor(selectedItem)
        )
        if (isAllSelected && !isSelected) {
          isAllSelected = false
        }
        if (isNoneSelected && isSelected) {
          isNoneSelected = false
        }
      }
      if (isNoneSelected) {
        return false
      }
      if (isAllSelected) {
        return true
      }
    }
    return 'indeterminate'
  }, [idExtractor, searchResults?.data, selected])

  const handleSelectAllChange = useCallback(() => {
    if (onSelectedChange) {
      if ((!checkboxState || checkboxState === 'indeterminate') && searchResults?.data) {
        onSelectedChange(searchResults.data)
      } else {
        onSelectedChange([])
      }
    }
  }, [checkboxState, onSelectedChange, searchResults?.data])

  const checkbox = useMemo(() => {
    if (selected) {
      return (
        <TableCell id={id && `${id}-header-select-all-cell`} sx={{width: '2rem'}}>
          <Checkbox
            id={id && `${id}-header-select-all-input`}
            size='small'
            checked={typeof checkboxState === 'boolean' ? checkboxState : false}
            indeterminate={checkboxState === 'indeterminate'}
            onChange={handleSelectAllChange}
          />
        </TableCell>
      )
    }
  }, [checkboxState, handleSelectAllChange, id, selected])

  return (
    <TableHead>
      <TableRow>
        {checkbox}
        {headerCells}
      </TableRow>
    </TableHead>
  )
}
